import React from "react";

/**
 * MaintenanceScreen — rendered when the backend reports
 *   { maintenance: true, owner_ok: false }
 * The owner (identified via OWNER_EMAIL server-side) bypasses this screen
 * automatically; anyone else sees this until MAINTENANCE_MODE is disabled.
 *
 * Design: Mirror of Certainty. Pure #000, 0.5px #808080 borders, Roboto Mono
 * for data, Inter for prose. No decorations, no rounded corners, no shadows.
 */
export default function MaintenanceScreen({ onOwnerLogin }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const loginUrl = `${backend}/api/auth/google/login`;
  const now = new Date().toISOString().replace("T", " · ").slice(0, 19) + " UTC";

  return (
    <div
      className="min-h-screen bg-black text-white ae-cursor"
      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="maintenance-screen"
    >
      <div className="max-w-[1120px] mx-auto px-8 py-10">
        {/* Header bar */}
        <div
          className="flex items-center justify-between pb-4"
          style={{ borderBottom: "0.5px solid #808080" }}
        >
          <div className="mono text-xs tracking-widest text-[#c0c0c0]">
            AUDITENGINE // OFFLINE FOR MAINTENANCE
          </div>
          <div className="mono text-[10px] text-[#808080]">{now}</div>
        </div>

        {/* Main block */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-0 mt-16">
          <div className="md:col-span-8">
            <div
              className="mono text-[10px] tracking-[0.3em] text-[#808080] mb-6"
              data-testid="maintenance-status-line"
            >
              STATUS · 503 · SERVICE_TEMPORARILY_UNAVAILABLE
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white"
              data-testid="maintenance-headline"
            >
              This workspace is<br />paused for maintenance.
            </h1>
            <p className="text-base text-[#c0c0c0] mt-8 max-w-[560px]">
              AuditEngine is intentionally offline. Public routes — including
              the audit registry, verification microsite, leaderboard, and
              regulator sandbox — are currently closed to the network. Access
              is restricted to the workspace owner.
            </p>

            {/* Owner login row */}
            <div
              className="mt-14 pt-6 flex items-center gap-6"
              style={{ borderTop: "0.5px solid #808080" }}
            >
              <a
                href={loginUrl}
                className="mono text-xs tracking-widest px-5 py-3 text-white hover:bg-white hover:text-black transition-colors"
                style={{ border: "0.5px solid #808080" }}
                data-testid="maintenance-owner-login-btn"
              >
                ▸ OWNER · AUTHENTICATE VIA GOOGLE
              </a>
              <div className="mono text-[10px] text-[#808080] max-w-[360px] leading-relaxed">
                Owner-only access. All other Google identities will be
                returned to this screen.
              </div>
            </div>
          </div>

          {/* Right-hand spec column */}
          <div className="md:col-span-4 md:pl-10 md:ml-10 mt-14 md:mt-0"
               style={{ borderLeft: "0.5px solid #808080" }}>
            <div className="mono text-[10px] tracking-[0.3em] text-[#808080] pb-4"
                 style={{ borderBottom: "0.5px solid #808080" }}>
              GATE · SPECIFICATION
            </div>
            <SpecRow k="mode" v="MAINTENANCE" />
            <SpecRow k="scope" v="ALL_PUBLIC_ROUTES" />
            <SpecRow k="allowlist" v="OAUTH · STATUS · HEARTBEAT" />
            <SpecRow k="owner_gate" v="EMAIL_MATCH · CASE_INSENSITIVE" />
            <SpecRow k="response" v="HTTP 503 · retry_after=3600s" />
            <SpecRow k="clearance" v="OWNER_ONLY" />
          </div>
        </div>

        {/* Footer */}
        <div
          className="mt-24 pt-4 flex items-center justify-between"
          style={{ borderTop: "0.5px solid #808080" }}
        >
          <div className="mono text-[10px] text-[#808080]">
            AUDITENGINE · DETERMINISTIC ESG COMPLIANCE INTELLIGENCE
          </div>
          <div className="mono text-[10px] text-[#808080]">SOVEREIGN · v1.0</div>
        </div>
      </div>
    </div>
  );
}

function SpecRow({ k, v }) {
  return (
    <div
      className="flex items-center justify-between py-3"
      style={{ borderBottom: "0.5px solid #808080" }}
    >
      <div className="mono text-[10px] tracking-widest text-[#808080] uppercase">{k}</div>
      <div className="mono text-[10px] tracking-widest text-white uppercase">{v}</div>
    </div>
  );
}
